/* ==========================================================================
   HEMP HEART BANNER SECTION — BEHAVIOR (v2)
   - Scroll reveal animation for content / product cards
   - SAFETY NET: elements already in view at load (or in edge-case iframes /
     previews where IntersectionObserver never fires) are force-shown after
     a short timeout, so text/icons can never get stuck invisible.
   - Mobile swipeable carousel with dot indicators for product blocks
   ========================================================================== */

class HempHeartBanner {
  constructor(root) {
    this.root = root;
    this.id = root.dataset.sectionId;
    this.track = root.querySelector('.hhb-products-track');
    this.imagesWrap = root.querySelector('.hhb-images');
    this.dotsWrap = root.querySelector('.hhb-carousel-dots');
    this.cards = root.querySelectorAll('.hhb-product-card');
    this.mobileBreakpoint = 749;

    this.onResize = this.debounce(this.onResize.bind(this), 150);

    this.init();
  }

  init() {
    this.setupRevealAnimation();
    this.setupCarousel();
    window.addEventListener('resize', this.onResize);
  }

  /* ---------------- Reveal on scroll ---------------- */

  setupRevealAnimation() {
    const animatedEls = this.root.querySelectorAll('.hhb-animate');
    if (!animatedEls.length) return;

    // Fallback for browsers without IntersectionObserver support.
    if (!('IntersectionObserver' in window)) {
      animatedEls.forEach((el) => el.classList.add('hhb-in-view'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('hhb-in-view');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -20px 0px' }
    );

    animatedEls.forEach((el, index) => {
      el.style.transitionDelay = `${index * 80}ms`;
      observer.observe(el);

      // Immediate check: if the element is already on-screen at load
      // (very common above-the-fold, and in narrow preview iframes),
      // don't wait on the observer — show it right away.
      const rect = el.getBoundingClientRect();
      const inViewNow =
        rect.top < (window.innerHeight || document.documentElement.clientHeight) &&
        rect.bottom > 0;
      if (inViewNow) {
        el.classList.add('hhb-in-view');
        observer.unobserve(el);
      }
    });

    // Hard safety net: whatever hasn't revealed itself within 1.2s
    // (theme-editor iframes, zero-height parents during load, etc.)
    // gets force-shown so content is never permanently hidden.
    setTimeout(() => {
      animatedEls.forEach((el) => {
        if (!el.classList.contains('hhb-in-view')) {
          el.classList.add('hhb-force-visible');
        }
      });
    }, 1200);
  }

  /* ---------------- Mobile carousel ---------------- */

  setupCarousel() {
    if (!this.track || !this.cards.length) return;

    this.buildDots();
    this.evaluateMode();

    this.track.addEventListener('scroll', () => this.debounceScroll(), {
      passive: true,
    });
  }

  buildDots() {
    if (!this.dotsWrap) return;
    this.dotsWrap.innerHTML = '';
    this.cards.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.classList.add('hhb-carousel-dot');
      dot.setAttribute('aria-label', `Go to product ${i + 1}`);
      if (i === 0) dot.classList.add('is-active');
      dot.addEventListener('click', () => this.scrollToCard(i));
      this.dotsWrap.appendChild(dot);
    });
  }

  scrollToCard(index) {
    const card = this.cards[index];
    if (!card || !this.track) return;
    const offset =
      card.offsetLeft - (this.track.clientWidth - card.clientWidth) / 2;
    this.track.scrollTo({ left: offset, behavior: 'smooth' });
  }

  debounceScroll() {
    clearTimeout(this._scrollTimer);
    this._scrollTimer = setTimeout(() => this.updateActiveDot(), 80);
  }

  updateActiveDot() {
    if (!this.dotsWrap || !this.track) return;
    const trackCenter = this.track.scrollLeft + this.track.clientWidth / 2;
    let closestIndex = 0;
    let closestDistance = Infinity;

    this.cards.forEach((card, i) => {
      const cardCenter = card.offsetLeft + card.clientWidth / 2;
      const distance = Math.abs(trackCenter - cardCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = i;
      }
    });

    const dots = this.dotsWrap.querySelectorAll('.hhb-carousel-dot');
    dots.forEach((dot, i) => dot.classList.toggle('is-active', i === closestIndex));
  }

  evaluateMode() {
    const isMobile = window.innerWidth <= this.mobileBreakpoint;
    if (this.imagesWrap) {
      this.imagesWrap.classList.toggle('hhb-mobile-carousel', isMobile);
    }
    if (this.dotsWrap) {
      this.dotsWrap.classList.toggle('is-active-mobile', isMobile);
    }
  }

  onResize() {
    this.evaluateMode();
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  }

  destroy() {
    window.removeEventListener('resize', this.onResize);
  }
}

/* ---------------- Init + Shopify theme editor support ---------------- */

function initHempHeartBanners(container = document) {
  container.querySelectorAll('.hhb-section[data-hhb-init="1"]').forEach((el) => {
    if (el._hhbInstance) return;
    el._hhbInstance = new HempHeartBanner(el);
  });
}

document.addEventListener('DOMContentLoaded', () => initHempHeartBanners());

// Re-init when Shopify theme editor injects/re-renders the section
document.addEventListener('shopify:section:load', (event) => {
  initHempHeartBanners(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  const el = event.target.querySelector('.hhb-section');
  if (el && el._hhbInstance) {
    el._hhbInstance.destroy();
    delete el._hhbInstance;
  }
});
